import type { TariffFeature } from 'src/types/tariff';
import type { PermissionMatrixLike } from 'src/utils/permissions';
import type { PlatformFeatureAccess } from 'src/utils/platform-features';

import type { TurnOutcome } from '../analytics';
import {
  AI_MODELS,
  CONTEXT_WINDOW_TOKENS,
  MAX_AGENT_ITERATIONS,
  MAX_HISTORY_MESSAGES,
  MAX_TOOL_RESULT_CHARS,
} from '../config';
import type { PageContext } from '../context/page-context';
import type { PrivacyGateway } from '../privacy/gateway';
import { buildSystemPrompt } from '../prompts/system-prompt';
import {
  completeChat,
  ContextOverflowError,
  type ChatMessage,
  type ChatTool,
  type ChatToolCall,
} from './llm';
import { isToolAvailable } from './feature-guard';
import { checkToolPermission } from './permission-guard';
import { toolRegistry } from './registry';
import type { ToolCatalog } from './registry';
import { buildScopeRefusal, isRequestInScope } from './scope-guard';
import { checkToolTariff } from './tariff-guard';
import type { AnyTool, ChatAttachment, ToolContext } from './tool-types';
import { buildClassifierInput } from './transcript';

// ----------------------------------------------------------------------

export type ToolEndStatus = 'done' | 'error' | 'denied' | 'rejected';

export type EngineEvent =
  | { type: 'phase'; phase: 'planning' | 'thinking' }
  | { type: 'tools-loaded'; names: string[] }
  | { type: 'tool-start'; callId: string; toolName: string; args: unknown }
  | { type: 'tool-end'; callId: string; toolName: string; status: ToolEndStatus }
  | { type: 'tool-attachment'; callId: string; toolName: string; attachment: ChatAttachment }
  | { type: 'assistant-message'; text: string }
  | { type: 'context-usage'; totalTokens: number }
  | { type: 'turn-end'; outcome: TurnOutcome };

export type ConfirmationRequest = {
  toolName: string;
  args: unknown;
};

export type EngineContext = {
  platformId: string | null;
  platformName?: string;
  userRole?: string;
  userName?: string;
  appLanguage: string;
  permissionMatrix: PermissionMatrixLike | null;
  canUseTariffFeature: (feature: TariffFeature) => boolean;
  /** The platform's edition: tools of features it lacks are invisible to the model. */
  features: PlatformFeatureAccess;
  /** Entities visible on the page the user is looking at (course, group…). */
  pageContext?: PageContext;
  /** Tools already loaded in this conversation — persists across turns. */
  loadedTools: Set<string>;
  privacy: PrivacyGateway;
  signal: AbortSignal;
  onEvent: (event: EngineEvent) => void;
  /** Resolves once the user approves or rejects a dangerous action in the UI. */
  requestConfirmation: (request: ConfirmationRequest) => Promise<boolean>;
  /**
   * Called after a mutating tool succeeds so the app refreshes its React Query
   * cache. `keys` are query-key prefixes; undefined means "invalidate all".
   */
  onMutationSuccess: (keys?: readonly (readonly unknown[])[]) => void;
};

// ----------------------------------------------------------------------

const LOAD_TOOLS_NAME = 'load_tools';

const LOAD_TOOLS_TOOL: ChatTool = {
  type: 'function',
  function: {
    name: LOAD_TOOLS_NAME,
    description:
      'Load the full parameter schemas of catalog tools before using them. ' +
      'Load only the tools you actually plan to call.',
    parameters: {
      type: 'object',
      properties: {
        names: {
          type: 'array',
          items: { type: 'string' },
          description: 'Exact tool names from the catalog',
        },
      },
      required: ['names'],
    },
  },
};

const toChatTool = (tool: AnyTool): ChatTool => ({
  type: 'function',
  function: {
    name: tool.name,
    description: tool.description,
    parameters: tool.parameters as unknown as Record<string, unknown>,
  },
});

const truncate = (raw: string): string =>
  raw.length > MAX_TOOL_RESULT_CHARS
    ? `${raw.slice(0, MAX_TOOL_RESULT_CHARS)}… [truncated, refine your query to see more]`
    : raw;

const toToolMessage = (callId: string, result: unknown): ChatMessage => {
  let content: string;
  try {
    content = JSON.stringify(result) ?? 'null';
  } catch {
    content = String(result);
  }
  return { role: 'tool', tool_call_id: callId, content: truncate(content) };
};

// ----------------------------------------------------------------------

/** Cheap routing step: pick the model tier that matches the request difficulty. */
const classifyRequest = async (
  userText: string,
  history: ChatMessage[],
  signal: AbortSignal
): Promise<string> => {
  try {
    const { message } = await completeChat({
      model: AI_MODELS.light,
      temperature: 0,
      signal,
      messages: [
        {
          role: 'system',
          content:
            'You route requests for an LMS team assistant. Judge the LATEST message in the ' +
            'context of the conversation. Answer with exactly one word:\n' +
            '"light" — greeting, small talk or a question that needs no platform data;\n' +
            '"standard" — a straightforward read-only lookup of platform data;\n' +
            '"advanced" — multi-step analysis, reports, comparisons, fuzzy entity matching, ' +
            'or ANY request that creates, modifies or deletes data.\n' +
            'A short reply that supplies details the assistant just asked for inherits the ' +
            'difficulty of the task being set up — details for a create/update flow are "advanced".',
        },
        { role: 'user', content: buildClassifierInput(userText, history) },
      ],
    });
    const word = (message.content ?? '').trim().toLowerCase();
    if (word.includes('light')) return AI_MODELS.light;
    if (word.includes('advanced')) return AI_MODELS.advanced;
    return AI_MODELS.standard;
  } catch {
    return AI_MODELS.standard;
  }
};

// ----------------------------------------------------------------------

/** The engine context plus the tools this turn may see — every lookup goes through `tools`. */
type TurnContext = EngineContext & { tools: ToolCatalog };

const handleLoadTools = (rawNames: unknown, ctx: TurnContext): unknown => {
  const names = Array.isArray(rawNames) ? rawNames.filter((n) => typeof n === 'string') : [];
  const loaded: string[] = [];

  const result = names.map((name) => {
    const tool = ctx.tools.get(name);
    if (!tool) {
      return { name, error: 'UNKNOWN_TOOL' };
    }
    ctx.loadedTools.add(name);
    loaded.push(name);
    const verdict = checkToolPermission(tool, ctx.permissionMatrix);
    const tariffVerdict = checkToolTariff(tool, ctx.canUseTariffFeature);
    const deniedReason = (() => {
      if (!verdict.allowed) {
        return `User lacks "${verdict.action}" permission on ${verdict.resource}`;
      }
      if (!tariffVerdict.allowed) {
        return `The platform's plan does not include the "${tariffVerdict.feature}" feature`;
      }
      return null;
    })();

    return {
      name,
      description: tool.description,
      parameters: tool.parameters,
      dangerous: tool.dangerous ?? false,
      allowed: deniedReason === null,
      ...(deniedReason === null ? {} : { denied_reason: deniedReason }),
    };
  });

  if (loaded.length) {
    ctx.onEvent({ type: 'tools-loaded', names: loaded });
  }
  return { tools: result };
};

const executeTool = async (
  callId: string,
  tool: AnyTool,
  args: unknown,
  ctx: TurnContext
): Promise<unknown> => {
  const verdict = checkToolPermission(tool, ctx.permissionMatrix);
  if (!verdict.allowed) {
    ctx.onEvent({ type: 'tool-end', callId, toolName: tool.name, status: 'denied' });
    return {
      error: 'PERMISSION_DENIED',
      message: `The current user lacks the "${verdict.action}" permission on ${verdict.resource}. Tell the user they are not allowed to perform this action. Do not retry.`,
    };
  }

  const tariffVerdict = checkToolTariff(tool, ctx.canUseTariffFeature);
  if (!tariffVerdict.allowed) {
    ctx.onEvent({ type: 'tool-end', callId, toolName: tool.name, status: 'denied' });
    return {
      error: 'TARIFF_FEATURE_UNAVAILABLE',
      message: `The "${tariffVerdict.feature}" feature is not included in the platform's current plan. Tell the user this action requires a plan upgrade and that they can contact support. Do not retry.`,
    };
  }

  // The model only ever saw placeholders — the user and the API get real values.
  const { args: realArgs, unknownPlaceholders } = ctx.privacy.unmaskArgs(args);
  if (unknownPlaceholders.length) {
    ctx.onEvent({ type: 'tool-end', callId, toolName: tool.name, status: 'error' });
    return {
      error: 'UNKNOWN_PLACEHOLDER',
      message: `These privacy placeholders do not exist in this conversation: ${unknownPlaceholders.join(', ')}. Use only placeholders exactly as they appeared in earlier messages or tool results — never invent or alter them. If a real value is missing, ask the user.`,
    };
  }

  if (tool.dangerous) {
    const approved = await ctx.requestConfirmation({ toolName: tool.name, args: realArgs });
    if (!approved) {
      ctx.onEvent({ type: 'tool-end', callId, toolName: tool.name, status: 'rejected' });
      return {
        error: 'USER_REJECTED',
        message: 'The user declined this action in the confirmation dialog. Do not retry.',
      };
    }
  }

  ctx.onEvent({ type: 'tool-start', callId, toolName: tool.name, args: realArgs });

  const toolCtx: ToolContext = {
    platformId: ctx.platformId,
    signal: ctx.signal,
    permissionMatrix: ctx.permissionMatrix,
    appLanguage: ctx.appLanguage,
  };
  try {
    const raw = await tool.execute(realArgs as never, toolCtx);
    // The axios layer returns backend errors as response bodies instead of throwing.
    const isErrorResult =
      raw !== null && typeof raw === 'object' && 'code' in raw && 'message' in raw;
    const projected = tool.project ? tool.project(raw as never) : raw;
    ctx.onEvent({
      type: 'tool-end',
      callId,
      toolName: tool.name,
      status: isErrorResult ? 'error' : 'done',
    });
    const attachment = !isErrorResult && tool.present ? tool.present(raw as never) : null;
    if (attachment) {
      ctx.onEvent({ type: 'tool-attachment', callId, toolName: tool.name, attachment });
    }
    // The app must reflect mutations immediately — refresh the query cache.
    if (!isErrorResult && tool.permission.action !== 'get') {
      ctx.onMutationSuccess(tool.invalidates);
    }
    return ctx.privacy.maskResult(projected ?? { ok: true });
  } catch (error) {
    ctx.onEvent({ type: 'tool-end', callId, toolName: tool.name, status: 'error' });
    return ctx.privacy.maskResult({
      error: 'EXECUTION_FAILED',
      message: error instanceof Error ? error.message : 'Unknown execution error',
    });
  }
};

const handleToolCall = async (
  toolCall: ChatToolCall,
  ctx: TurnContext,
  seenCalls: Set<string>
): Promise<ChatMessage> => {
  if (toolCall.type !== 'function') {
    return toToolMessage(toolCall.id, { error: 'UNSUPPORTED_TOOL_CALL_TYPE' });
  }

  // Loop protection: an identical repeated call would return identical data.
  const signature = `${toolCall.function.name}:${toolCall.function.arguments ?? ''}`;
  if (toolCall.function.name !== LOAD_TOOLS_NAME && seenCalls.has(signature)) {
    return toToolMessage(toolCall.id, {
      error: 'REPEATED_CALL',
      message:
        'You already called this tool with identical arguments in this turn. Reuse the previous result. If you need a different capability (e.g. deleting or updating), load that tool via load_tools and call it instead.',
    });
  }
  seenCalls.add(signature);

  let args: unknown = {};
  try {
    args = toolCall.function.arguments ? JSON.parse(toolCall.function.arguments) : {};
  } catch {
    return toToolMessage(toolCall.id, {
      error: 'INVALID_ARGUMENTS',
      message: 'Tool arguments were not valid JSON. Fix the arguments and retry.',
    });
  }

  const name = toolCall.function.name;

  if (name === LOAD_TOOLS_NAME) {
    const names = (args as { names?: unknown }).names;
    return toToolMessage(toolCall.id, handleLoadTools(names, ctx));
  }

  const tool = ctx.tools.get(name);
  if (!tool) {
    return toToolMessage(toolCall.id, {
      error: 'UNKNOWN_TOOL',
      message: `Tool "${name}" does not exist. Check the catalog and call ${LOAD_TOOLS_NAME} first.`,
    });
  }

  return toToolMessage(toolCall.id, await executeTool(toolCall.id, tool, args, ctx));
};

// ----------------------------------------------------------------------

/**
 * Runs one full agent turn: route → plan → (load tools → execute)* → answer.
 * Returns the updated conversation history (system prompt excluded — it is
 * rebuilt fresh on every turn). The history stays masked; only the events
 * shown to the user carry real values.
 */
export const runAgentTurn = async (options: {
  history: ChatMessage[];
  userText: string;
  ctx: EngineContext;
}): Promise<ChatMessage[]> => {
  const { history } = options;
  const ctx: TurnContext = {
    ...options.ctx,
    tools: toolRegistry.scope((tool) => isToolAvailable(tool, options.ctx.features)),
  };
  const userText = ctx.privacy.maskText(options.userText);

  ctx.onEvent({ type: 'phase', phase: 'planning' });
  const trimmedHistory = history.slice(-MAX_HISTORY_MESSAGES);

  const [inScope, model] = await Promise.all([
    isRequestInScope({
      userText,
      history: trimmedHistory,
      edition: ctx.features.edition,
      signal: ctx.signal,
    }),
    classifyRequest(userText, trimmedHistory, ctx.signal),
  ]);

  // Off-topic request: decline politely and skip the whole agent loop.
  if (!inScope) {
    const refusal = await buildScopeRefusal({
      userText,
      platformName: ctx.platformName,
      appLanguage: ctx.appLanguage,
      edition: ctx.features.edition,
      signal: ctx.signal,
    });
    ctx.onEvent({ type: 'assistant-message', text: ctx.privacy.unmaskText(refusal) });
    ctx.onEvent({ type: 'turn-end', outcome: 'refused' });
    return [
      ...trimmedHistory,
      { role: 'user', content: userText },
      { role: 'assistant', content: refusal },
    ];
  }

  const messages: ChatMessage[] = [...trimmedHistory, { role: 'user', content: userText }];

  ctx.onEvent({ type: 'phase', phase: 'thinking' });

  const seenCalls = new Set<string>();
  let contextTokens = 0;

  for (let iteration = 0; iteration < MAX_AGENT_ITERATIONS; iteration += 1) {
    if (contextTokens >= CONTEXT_WINDOW_TOKENS) {
      throw new ContextOverflowError();
    }

    const activeTools: ChatTool[] = [
      LOAD_TOOLS_TOOL,
      ...Array.from(ctx.loadedTools)
        .map((name) => ctx.tools.get(name))
        .filter((tool): tool is AnyTool => Boolean(tool))
        .map(toChatTool),
    ];

    const system: ChatMessage = {
      role: 'system',
      content: buildSystemPrompt({
        catalog: ctx.tools.buildCatalog(),
        features: ctx.features,
        appLanguage: ctx.appLanguage,
        platformName: ctx.platformName,
        userRole: ctx.userRole,
        userName: ctx.userName,
        pageContext: ctx.pageContext,
      }),
    };

    const { message: assistant, usage } = await completeChat({
      model,
      messages: [system, ...messages],
      tools: activeTools,
      signal: ctx.signal,
    });

    if (usage) {
      contextTokens = usage.totalTokens;
      ctx.onEvent({ type: 'context-usage', totalTokens: usage.totalTokens });
    }

    messages.push(assistant as ChatMessage);

    const toolCalls = assistant.tool_calls ?? [];
    if (!toolCalls.length) {
      ctx.onEvent({
        type: 'assistant-message',
        text: ctx.privacy.unmaskText(assistant.content ?? ''),
      });
      ctx.onEvent({ type: 'turn-end', outcome: 'answered' });
      return messages;
    }

    // Tool calls run sequentially: later calls often depend on earlier results.
    for (const toolCall of toolCalls) {
      messages.push(await handleToolCall(toolCall, ctx, seenCalls));
    }

    ctx.onEvent({ type: 'phase', phase: 'thinking' });
  }

  if (contextTokens >= CONTEXT_WINDOW_TOKENS) {
    throw new ContextOverflowError();
  }

  // Tool budget exhausted — force a final, tool-free summary of progress so far.
  const { message: summary, usage: summaryUsage } = await completeChat({
    model,
    messages: [
      {
        role: 'system',
        content: `You ran out of the tool budget for this turn. Briefly summarize what you found and what is still missing. Respond in the user's language (fallback: ${ctx.appLanguage}).`,
      },
      ...messages,
    ],
    signal: ctx.signal,
  });

  if (summaryUsage) {
    ctx.onEvent({ type: 'context-usage', totalTokens: summaryUsage.totalTokens });
  }

  messages.push({ role: 'assistant', content: summary.content ?? '' });
  ctx.onEvent({ type: 'assistant-message', text: ctx.privacy.unmaskText(summary.content ?? '') });
  ctx.onEvent({ type: 'turn-end', outcome: 'budget_exhausted' });
  return messages;
};
